// Type3 Mock Data - Class View with merged cells
// This is for Class View (MergedCellTimetableGrid)
// School: Hollins - 3 Year Groups (7, 8, 9) x 8 rows each = 24 rows
// Cells from different year groups cannot merge together
// Cell shows: Teacher, Subject, Room
// Popup shows: Subject and Class (title), Teacher, Room, Period/Lesson Date, Period/Lesson Timing

export interface MergedCellData {
  id: string;
  label: string;
  color: string;
  rowSpan: number; // Number of groups this cell spans (1-8, but cannot cross year group boundaries)
  isUnavailable?: boolean;
  popupData: {
    title: string;
    subtitle?: string;
    details: { label: string; value: string }[];
  };
}

// Each period column contains an array of cells that fill all groups
export interface PeriodColumn {
  period: number;
  cells: MergedCellData[]; // Cells fill from top to bottom, their rowSpans must sum to total groups
}

export interface DayData {
  week: 'A' | 'B';
  day: string;
  periodColumns: PeriodColumn[];
}

export interface Type3Data {
  groups: string[]; // Row titles (Year 7 A-H, Year 8 A-H, Year 9 A-H)
  weeks: ('A' | 'B')[];
  days: string[];
  periodsPerDay: number;
  dayData: DayData[];
}

// Subject colors - each subject has a unique color
const subjectColors: Record<string, string> = {
  Eng: '#10B981', // Green
  Mat: '#3B82F6', // Blue
  Sci: '#8B5CF6', // Purple
  DT: '#F59E0B',  // Amber
  His: '#EF4444', // Red
  MFL: '#0EA5E9', // Sky
  Geo: '#EC4899', // Pink
  Art: '#6366F1', // Indigo
};

// Full subject names
const subjectFullNames: Record<string, string> = {
  Eng: 'English',
  Mat: 'Mathematics',
  Sci: 'Science',
  DT: 'Design Technology',
  His: 'History',
  MFL: 'Modern Foreign Languages',
  Geo: 'Geography',
  Art: 'Art',
};

// Teachers by subject with full names
interface TeacherInfo {
  initials: string;
  fullName: string;
  subject: string;
}

const allTeachers: TeacherInfo[] = [
  // English Teachers
  { initials: 'SHW', fullName: 'Mr S Howard', subject: 'Eng' },
  { initials: 'NTA', fullName: 'Ms N Taylor', subject: 'Eng' },
  { initials: 'SWA', fullName: 'Mrs S Walsh', subject: 'Eng' },
  // Maths Teachers
  { initials: 'JGA', fullName: 'Mr J Garcia', subject: 'Mat' },
  { initials: 'NBR', fullName: 'Mrs N Brown', subject: 'Mat' },
  { initials: 'DHA', fullName: 'Mr D Hall', subject: 'Mat' },
  // Science Teachers
  { initials: 'ECA', fullName: 'Dr E Carter', subject: 'Sci' },
  { initials: 'SBM', fullName: 'Mr S Bloom', subject: 'Sci' },
  // Design Teachers
  { initials: 'SSC', fullName: 'Mr S Scott', subject: 'DT' },
  { initials: 'EBA', fullName: 'Ms E Baker', subject: 'DT' },
  // History Teachers
  { initials: 'CWA', fullName: 'Mr C Watson', subject: 'His' },
  { initials: 'ASA', fullName: 'Ms A Sanders', subject: 'His' },
  // MFL Teachers
  { initials: 'CKI', fullName: 'Mrs C King', subject: 'MFL' },
  { initials: 'JGR', fullName: 'Mr J Green', subject: 'MFL' },
  { initials: 'HDO', fullName: 'Ms H Donovan', subject: 'MFL' },
  // Geography Teachers
  { initials: 'TDU', fullName: 'Mr T Duncan', subject: 'Geo' },
  { initials: 'NJO', fullName: 'Mrs N Jones', subject: 'Geo' },
  { initials: 'HUM', fullName: 'Dr H Humphrey', subject: 'Geo' },
  // Art Teachers
  { initials: 'LKE', fullName: 'Ms L Kelly', subject: 'Art' },
  { initials: 'PDR', fullName: 'Mr P Drew', subject: 'Art' },
  { initials: 'LWH', fullName: 'Mrs L White', subject: 'Art' },
];

// All rooms with capacities
interface RoomInfo {
  name: string;
  capacity: number;
}

const allRooms: RoomInfo[] = [
  { name: 'GYM', capacity: 120 },
  { name: 'Hall', capacity: 200 },
  { name: 'LRC', capacity: 60 },
  ...Array.from({ length: 30 }, (_, i) => ({
    name: `R${i + 1}`,
    capacity: 25 + Math.floor((i % 4) * 5), // Capacity varies between 25-40
  })),
];

// Subjects array
const subjects = Object.keys(subjectColors);

// Year groups for Type3 (7, 8, 9)
const yearGroupsType3 = [7, 8, 9];
const rowsPerYearGroup = 8; // A-H per year group

// Bands and Sets for Years 7-9
const bandXSets = ['H', 'O', 'L'];
const bandYSets = ['I', 'N', 'S'];
const allSets = [...bandXSets, ...bandYSets];

// Period times
const periodTimes = [
  { start: '09.05', end: '10.05' },
  { start: '10.05', end: '11.05' },
  { start: '11.20', end: '12.20' },
  { start: '13.00', end: '14.00' },
  { start: '14.00', end: '15.00' },
];

// Dates for January 2026
const weekDates: Record<string, Record<string, string>> = {
  A: {
    Monday: 'Mon 5th Jan 2026',
    Tuesday: 'Tue 6th Jan 2026',
    Wednesday: 'Wed 7th Jan 2026',
    Thursday: 'Thu 8th Jan 2026',
    Friday: 'Fri 9th Jan 2026',
  },
  B: {
    Monday: 'Mon 12th Jan 2026',
    Tuesday: 'Tue 13th Jan 2026',
    Wednesday: 'Wed 14th Jan 2026',
    Thursday: 'Thu 15th Jan 2026',
    Friday: 'Fri 16th Jan 2026',
  },
};

// Seeded random for consistency
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// Generate groups: Year 7 A-H, Year 8 A-H, Year 9 A-H
const groups = yearGroupsType3.flatMap((year) =>
  ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((letter) => `${year}${letter}`)
);

// Create a merged cell for Class View
// Cell shows: Teacher, Subject, Room
// Popup shows: Subject and Class (title), Teacher, Room, Period/Lesson Date, Period/Lesson Timing
const createMergedCell = (
  id: string,
  subject: string,
  yearGroup: number,
  setLetters: string[], // Can be multiple sets if merged
  room: RoomInfo,
  teacher: TeacherInfo,
  rowSpan: number,
  week: 'A' | 'B',
  day: string,
  period: number,
  isUnavailable = false
): MergedCellData => {
  const subjectCode = subject;
  const subjectName = subjectFullNames[subject] || subject;
  const color = subjectColors[subject] || '#94A3B8';
  const time = periodTimes[period - 1];
  const date = weekDates[week][day];

  // Cell label for Class View: Teacher, Subject, Room
  const label = `${teacher.initials}\n${subjectCode}\n${room.name}`;

  // Build class display
  const classDisplay = setLetters.length > 1 
    ? setLetters.map((s) => `${yearGroup}${s}`).join('/')
    : `${yearGroup}${setLetters[0]}`;

  return {
    id,
    label,
    color,
    rowSpan,
    isUnavailable,
    popupData: {
      title: `${subjectName} ${classDisplay}`,
      subtitle: subjectCode,
      details: [
        { label: 'Teacher', value: teacher.fullName },
        { label: 'Room', value: room.name },
        { label: 'Date', value: date },
        { label: 'Time', value: `${time.start} - ${time.end}` },
      ],
    },
  };
};

// Generate cells for a period column within a single year group (8 rows)
// Cells cannot cross year group boundaries
const generatePeriodCellsForYearGroup = (
  week: 'A' | 'B',
  day: string,
  period: number,
  yearGroup: number,
  yearGroupIndex: number,
  dayIndex: number
): MergedCellData[] => {
  const cells: MergedCellData[] = [];
  let remainingRows = rowsPerYearGroup;
  let cellIndex = 0;

  while (remainingRows > 0) {
    const seed = yearGroupIndex * 10000 + dayIndex * 1000 + period * 100 + cellIndex + (week === 'B' ? 50000 : 0);

    // Determine span (1-3 rows, but not more than remaining)
    const maxSpan = Math.min(3, remainingRows);
    const span = Math.floor(seededRandom(seed) * maxSpan) + 1;

    // Determine if this is an empty slot (about 15% chance)
    const isEmpty = seededRandom(seed + 1) < 0.15;

    if (isEmpty) {
      cells.push({
        id: `${week}-${day}-p${period}-y${yearGroup}-cell${cellIndex}-empty`,
        label: '',
        color: 'transparent',
        rowSpan: span,
        popupData: {
          title: 'Free Period',
          details: [],
        },
      });
    } else {
      // Get subject and teacher
      const subjectIndex = Math.floor(seededRandom(seed + 2) * subjects.length);
      const subject = subjects[subjectIndex];
      
      // Get teachers for this subject
      const subjectTeachers = allTeachers.filter((t) => t.subject === subject);
      const teacher = subjectTeachers[Math.floor(seededRandom(seed + 3) * subjectTeachers.length)];
      
      const room = allRooms[Math.floor(seededRandom(seed + 4) * allRooms.length)];

      // Determine set letters based on span
      // If single cell: one set letter
      // If merged: multiple set letters
      const setLetters: string[] = [];
      for (let i = 0; i < span; i++) {
        const setIndex = Math.floor(seededRandom(seed + 5 + i) * allSets.length);
        const setLetter = allSets[setIndex];
        if (!setLetters.includes(setLetter)) {
          setLetters.push(setLetter);
        }
      }

      // Ensure at least one set letter
      if (setLetters.length === 0) {
        setLetters.push(allSets[0]);
      }

      cells.push(
        createMergedCell(
          `${week}-${day}-p${period}-y${yearGroup}-cell${cellIndex}`,
          subject,
          yearGroup,
          setLetters,
          room,
          teacher,
          span,
          week,
          day,
          period,
          // Mark some as unavailable (about 3%)
          seededRandom(seed + 10) < 0.03
        )
      );
    }

    remainingRows -= span;
    cellIndex++;
  }

  return cells;
};

// Generate cells for an entire period column (all 24 rows across 3 year groups)
const generatePeriodCells = (
  week: 'A' | 'B',
  day: string,
  period: number,
  dayIndex: number
): MergedCellData[] => {
  const allCells: MergedCellData[] = [];

  // Generate cells for each year group separately (no merging across year groups)
  yearGroupsType3.forEach((yearGroup, yearGroupIndex) => {
    const yearGroupCells = generatePeriodCellsForYearGroup(
      week,
      day,
      period,
      yearGroup,
      yearGroupIndex,
      dayIndex
    );
    allCells.push(...yearGroupCells);
  });

  return allCells;
};

const generateDayData = (): DayData[] => {
  const weeks: ('A' | 'B')[] = ['A', 'B'];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const periodsPerDay = 5;

  const result: DayData[] = [];

  weeks.forEach((week) => {
    dayNames.forEach((day, dayIndex) => {
      const periodColumns: PeriodColumn[] = [];

      for (let period = 1; period <= periodsPerDay; period++) {
        periodColumns.push({
          period,
          cells: generatePeriodCells(week, day, period, dayIndex),
        });
      }

      result.push({
        week,
        day,
        periodColumns,
      });
    });
  });

  return result;
};

export const type3MockData: Type3Data = {
  groups,
  weeks: ['A', 'B'],
  days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  periodsPerDay: 5,
  dayData: generateDayData(),
};

export default type3MockData;
